// Championship service
// -------------------------------------------------------------
// Tries the real /api/ext/championships endpoints first (SFB-1105 backend
// branch). When the request fails (404 / 500 because the backend isn't on
// dev yet, or an explicit toggle), falls back to the local mock so the UI
// is fully developable end-to-end.
//
// Once SFB-1105 lands on dev/qa/master, the mock fallback path is dead code
// — keep `setMockMode(false)` as the global default and the live API takes
// over. Delete the mock entirely after a release cycle.

import ChampionshipRepository from '@/repositories/championship';
import mock from '@/services/championship.mock';

let forceMockMode = false;

export function setMockMode(enabled) {
  forceMockMode = !!enabled;
}

export function isMockMode() {
  return forceMockMode;
}

function isNetworkOrServerError(err) {
  if (!err) return false;
  if (!err.response) return true; // network failure
  const s = err.response.status;
  return s === 404 || s === 501 || s >= 500;
}

export async function getChampionshipOverview(championshipId) {
  if (forceMockMode) return mock.makeMockOverview(championshipId);
  try {
    const res = await ChampionshipRepository.getChampionshipOverview(championshipId);
    return res.data;
  } catch (err) {
    if (isNetworkOrServerError(err)) {
      console.warn('[championship] real overview unavailable, using mock');
      return mock.makeMockOverview(championshipId);
    }
    throw err;
  }
}

export async function getChampionshipBracket(championshipId) {
  if (forceMockMode) return mock.makeMockBracket(championshipId);
  try {
    const res = await ChampionshipRepository.getChampionshipBracket(championshipId);
    return res.data;
  } catch (err) {
    if (isNetworkOrServerError(err)) {
      console.warn('[championship] real bracket unavailable, using mock');
      return mock.makeMockBracket(championshipId);
    }
    throw err;
  }
}

export async function listChampionshipParticipants(championshipId) {
  if (forceMockMode) return mock.makeMockParticipants();
  try {
    const res = await ChampionshipRepository.listChampionshipParticipants(championshipId);
    return res.data;
  } catch (err) {
    if (isNetworkOrServerError(err)) {
      console.warn('[championship] real participants unavailable, using mock');
      return mock.makeMockParticipants();
    }
    throw err;
  }
}

// Helper: group bracket matches by stageOrder so the UI can render columns.
export function groupMatchesByStage(bracket, overview) {
  if (!bracket?.matches?.length || !overview?.stages?.length) return [];
  const stageById = new Map(overview.stages.map((s) => [s.id, s]));
  const buckets = new Map();
  bracket.matches.forEach((m) => {
    const stage = stageById.get(m.stageId);
    const order = stage ? stage.stageOrder : 0;
    if (!buckets.has(order)) buckets.set(order, { stage, matches: [] });
    buckets.get(order).matches.push(m);
  });
  return Array.from(buckets.values())
    .sort((a, b) => (a.stage?.stageOrder ?? 0) - (b.stage?.stageOrder ?? 0))
    .map(({ stage, matches }) => ({
      stage,
      matches: matches.sort((a, b) => (a.matchOrder ?? 0) - (b.matchOrder ?? 0)),
    }));
}

// Stage-type → human label fallback when the backend doesn't ship a localized name.
const STAGE_LABELS = {
  QUALIFICATION: 'Qualification',
  ROUND_OF_32: 'Round of 32',
  ROUND_OF_16: 'Round of 16',
  QUARTERFINALS: 'Quarterfinal',
  SEMIFINALS: 'Semifinal',
  FINALS: 'Final',
  THIRD_PLACE: 'Third place',
  CUSTOM: 'Stage',
};

export function stageLabel(stage) {
  if (!stage) return '';
  return STAGE_LABELS[stage.stageType] || stage.stageType || `Stage ${stage.stageOrder}`;
}

// Pick the "current" stage to show on the Members tab and to source match
// details from. Preference order: RUNNING > NOT_STARTED (next up) > the most
// recently FINISHED stage. Falls back to the first stage if none look stale-
// less. Returns null when there are no stages at all.
export function findActiveStage(overview) {
  const stages = overview?.stages || [];
  if (!stages.length) return null;
  const ordered = [...stages].sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0));
  const running = ordered.find((s) => s.tournamentState === 'RUNNING');
  if (running) return running;
  const upcoming = ordered.find((s) => s.tournamentState === 'NOT_STARTED');
  if (upcoming) return upcoming;
  const lastFinished = [...ordered].reverse().find((s) => s.tournamentState === 'FINISHED');
  return lastFinished || ordered[0];
}

// True if the broker admin can advance the given stage (not yet advanced AND
// the stage's underlying tournament is FINISHED). Server enforces this too;
// frontend uses it to disable the CTA visually.
export function canAdvanceStage(stage) {
  if (!stage) return false;
  if (stage.advancementCompletedAt) return false;
  return stage.tournamentState === 'FINISHED';
}

// Number of matches a given stage type should contain when fully drawn —
// used to generate placeholder match cards for stages where the backend
// hasn't yet seeded matches (e.g. later rounds before earlier ones finish).
const MATCHES_PER_STAGE_TYPE = {
  ROUND_OF_32: 16,
  ROUND_OF_16: 8,
  QUARTERFINALS: 4,
  SEMIFINALS: 2,
  FINALS: 1,
  THIRD_PLACE: 1,
};

export function expectedMatchCount(stage, fallbackPrevCount) {
  if (!stage) return 0;
  const fromType = MATCHES_PER_STAGE_TYPE[stage.stageType];
  if (fromType) return fromType;
  // CUSTOM / QUALIFICATION fall back to half the previous stage's count.
  if (fallbackPrevCount && fallbackPrevCount > 0) return Math.ceil(fallbackPrevCount / 2);
  return 0;
}

// Like groupMatchesByStage but pads empty stages with placeholder match
// objects so the bracket always renders the full silhouette. Placeholders
// have `placeholder: true` so the match card can render an "upcoming" empty
// state with two TBD slots.
export function groupMatchesByStageWithPlaceholders(bracket, overview) {
  const groups = groupMatchesByStage(bracket, overview);
  if (!groups.length && (overview?.stages?.length || 0) > 0) {
    // Bracket has zero matches yet — synthesize all stages as placeholders.
    const stages = [...overview.stages].sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0));
    let prev = 0;
    return stages.map((stage) => {
      const count = expectedMatchCount(stage, prev * 2 || undefined) || 1;
      prev = count;
      return {
        stage,
        matches: Array.from({ length: count }, (_, i) => placeholderMatch(stage, i + 1)),
      };
    });
  }
  // For each existing group, pad with placeholders if its match count is
  // less than expected.
  let prev = 0;
  const padded = groups.map((g) => {
    const expected = expectedMatchCount(g.stage, prev * 2 || undefined);
    prev = expected || g.matches.length;
    if (!expected || g.matches.length >= expected) return g;
    const startOrder = g.matches.length + 1;
    const placeholders = Array.from({ length: expected - g.matches.length }, (_, i) =>
      placeholderMatch(g.stage, startOrder + i),
    );
    return { stage: g.stage, matches: [...g.matches, ...placeholders] };
  });
  // Also add stages that exist in overview but were skipped because they
  // had no matches at all.
  const seenStageIds = new Set(padded.map((g) => g.stage?.id));
  (overview?.stages || []).forEach((stage) => {
    if (seenStageIds.has(stage.id)) return;
    const lastWithMatches = [...padded].reverse().find((g) => g.matches.length > 0);
    const fallback = lastWithMatches ? Math.ceil(lastWithMatches.matches.length / 2) : 1;
    const count = expectedMatchCount(stage, fallback) || 1;
    padded.push({
      stage,
      matches: Array.from({ length: count }, (_, i) => placeholderMatch(stage, i + 1)),
    });
  });
  return padded.sort((a, b) => (a.stage?.stageOrder ?? 0) - (b.stage?.stageOrder ?? 0));
}

function placeholderMatch(stage, order) {
  return {
    id: `placeholder-${stage?.id ?? 'x'}-${order}`,
    stageId: stage?.id ?? null,
    matchOrder: order,
    participantAId: null,
    participantBId: null,
    userAId: null,
    userBId: null,
    winnerParticipantId: null,
    winnerUserId: null,
    scoreA: null,
    scoreB: null,
    status: 'PENDING',
    nextMatchId: null,
    nextMatchSlot: null,
    placeholder: true,
  };
}

// Resolve the partner accent colour across all three apps. Returns a #-prefixed
// hex string, falling back to the default brand red. Reactive when wrapped in
// a Vue computed because it just reads from store state.
export function resolveAccentColor(store) {
  const s = store?.state || {};
  const brokerColor = s.broker?.brokerInfo?.partner?.mainColor;
  if (brokerColor) return `#${String(brokerColor).replace('#', '')}`;
  const ibColor = s.ib?.partner?.primaryColor;
  if (ibColor) return `#${String(ibColor).replace('#', '')}`;
  const clubColor = s.clubMember?.club?.primaryColor;
  if (clubColor) return `#${String(clubColor).replace('#', '')}`;
  const tournamentColor = s.tournament?.tournamentSettings?.colors?.primary;
  if (tournamentColor) return `#${String(tournamentColor).replace('#', '')}`;
  return '#c03131';
}

// Lots represented by one "goal" in the championship score. TEMPORARY hardcode:
// championship 2 uses 10 lots/goal, everything else 100. The backend will expose
// this as a championship param — once it does, read it off the championship
// (e.g. championship.lotsPerGoal) and drop the LOTS_PER_GOAL_OVERRIDES map.
const LOTS_PER_GOAL_DEFAULT = 100;
const LOTS_PER_GOAL_OVERRIDES = { 2: 10 };
export function getLotsPerGoal(championship) {
  const id = championship?.id;
  return LOTS_PER_GOAL_OVERRIDES[id] ?? LOTS_PER_GOAL_DEFAULT;
}

export default {
  setMockMode,
  isMockMode,
  getChampionshipOverview,
  getChampionshipBracket,
  listChampionshipParticipants,
  groupMatchesByStage,
  groupMatchesByStageWithPlaceholders,
  expectedMatchCount,
  stageLabel,
  findActiveStage,
  canAdvanceStage,
  resolveAccentColor,
  getLotsPerGoal,
};
